import { Alert, List, Text } from '@mantine/core';
import { AlertTriangle } from 'react-feather';
import type { FormTimeSlot } from './types';
import {
  formatLastTried,
  timeSlotAttemptsText,
  timeSlotFailureMessage,
  timeSlotFailureTitle,
  unsavedTimeSlotLines,
} from './timeSlotCreation';

interface TimeSlotSaveFailureAlertProps {
  cohortName: string;
  // Every slot of the wizard, in save order, and the ids of the unsaved ones.
  timeSlots: FormTimeSlot[];
  unsavedSlotIds: string[];
  // How many times the slots were sent, and when the last try ran (ISO).
  attempts?: number;
  lastTriedAt?: string;
}

// MED-1268: shown in the wizard when the cohort was created but one or more
// time slots were not saved. It stays until the slots save or the CM leaves.
export function TimeSlotSaveFailureAlert({
  cohortName,
  timeSlots,
  unsavedSlotIds,
  attempts,
  lastTriedAt,
}: TimeSlotSaveFailureAlertProps) {
  const lines = unsavedTimeSlotLines(timeSlots, unsavedSlotIds);
  const totalSlots = timeSlots.length;
  const lastTried = lastTriedAt ? new Date(lastTriedAt) : null;
  return (
    <Alert
      color="red"
      variant="light"
      icon={<AlertTriangle size={18} />}
      title={timeSlotFailureTitle(lines.length, totalSlots)}
      mb="md"
    >
      <Text size="sm" mb="xs">
        The cohort {cohortName} was created. {timeSlotFailureMessage(lines.length, totalSlots)}
      </Text>
      <List size="sm" mb="xs">
        {lines.map(line => (
          <List.Item key={line}>{line}</List.Item>
        ))}
      </List>
      <Text size="sm">
        Press Retry Time Slots to try again. Only these slots are sent again, in this order, so each slot
        keeps its slot number. Or press Go to Cohort and add them in Edit Cohort Details. This browser keeps
        this list until the slots are saved, you discard the list, or you log out. Cancel and Keep List also
        keep it.
      </Text>
      {attempts && lastTried && !Number.isNaN(lastTried.getTime()) ? (
        <Text size="xs" c="dimmed" mt="xs">
          {timeSlotAttemptsText(attempts)} Last try at {formatLastTried(lastTried)}.
        </Text>
      ) : null}
    </Alert>
  );
}
